import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import type { RecentlySharedMap } from '@birdi/js-sdk/src/public/maps';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';
import { getRelativeTime } from '../utils';
import LinkSVG from '@birdi/icons/link.svg';
import CopySVG from '@birdi/icons/copy.svg';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const DISPLAY_LIMIT = 5;

export const RecentlySharedMaps: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [items, setItems] = useState<RecentlySharedMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedMapId, setCopiedMapId] = useState<string | null>(null);

  const hasData = !loading && items.length > 0;
  const isExpandDisabled = !hasData || loading;

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  useEffect(() => {
    if (!meta.organisation?.id || !workspaceId) {
      setItems([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await mapsAPI.getRecentlySharedMaps(
          stroot('daeph4'),
          workspaceId,
          { limit: DISPLAY_LIMIT },
        );
        setItems(res.body ?? []);
      } catch (e) {
        console.error('Error fetching recently shared maps:', e);
        setItems([]);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id, workspaceId]);

  const handleMapClick = (mapId: string) => {
    posthog?.capture('dashboard recently shared maps clicked', { mapId });
    navigate(`/map/next/${mapId}`);
  };

  const handleCopyLink = (e: React.MouseEvent, item: RecentlySharedMap) => {
    e.stopPropagation();
    if (!item.shareUrl) return;
    navigator.clipboard.writeText(item.shareUrl).then(() => {
      setCopiedMapId(item.mapId);
      posthog?.capture('dashboard recently shared maps copy link', {
        mapId: item.mapId,
      });
      setTimeout(() => setCopiedMapId(null), 2000);
    });
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
      id={suppressIntercomAnchor ? undefined : 'recently-shared-maps'}
    >
      {/* Header: light blue, title + reorder */}
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5em;
        `}
      >
        <div css={css`display: flex; align-items: center; gap: 0.5em;`}>
          <ReorderingIcon
            isReorderMode={isReorderMode}
            isHovered={isHovered}
            isDragging={isDragging}
          />
          <span>Recently shared maps</span>
        </div>
      </div>

      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
            font-size: 0.9em;
          `}
        >
          Maps shared with your team or external users
        </div>

        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
              color: ${theme.mono50};
            `}
          >
            Loading...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div css={css`color: ${theme.mono60};`}>
              <strong>Maps you've shared will appear here.</strong>
            </div>
            <div
              css={css`
                margin-top: 0.5em;
                color: ${theme.mono50};
              `}
            >
              Share a map to start tracking collaboration and access in this
              space.
            </div>
          </div>
        )}

        {hasData && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0;
            `}
          >
            {items.map((item) => (
              <div
                key={item.mapId}
                onClick={() => handleMapClick(item.mapId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMapClick(item.mapId);
                  }
                }}
                css={css`
                  display: grid;
                  grid-template-columns: auto 1fr auto;
                  align-items: start;
                  gap: 0.75em 1em;
                  padding: 0.75em;
                  border-bottom: 1px solid ${theme.mono20};
                  cursor: pointer;
                  transition: background 0.15s;
                  &:hover {
                    background: ${theme.mono10};
                  }
                  &:last-child {
                    border-bottom: none;
                  }
                `}
              >
                {/* Column 1: User profile pic */}
                <div
                  css={css`
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: ${theme.lightOcean};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.8em;
                    overflow: hidden;
                    color: ${theme.deepOcean};
                  `}
                >
                  {item.sharerProfilePic ? (
                    <img
                      src={item.sharerProfilePic}
                      alt=""
                      css={css`
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                      `}
                    />
                  ) : (
                    <span>
                      {item.sharerFname?.[0] ?? ''}
                      {item.sharerLname?.[0] ?? ''}
                    </span>
                  )}
                </div>

                {/* Column 2: Left-aligned — name, map, time */}
                <div
                  css={css`
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 0.25em;
                    text-align: left;
                  `}
                >
                  <div
                    css={css`
                      color: ${theme.mono70};
                      font-size: 0.9em;
                    `}
                  >
                    {[item.sharerFname, item.sharerLname]
                      .filter(Boolean)
                      .join(' ')}{' '}
                    shared a map:
                  </div>
                  <div
                    css={css`
                      font-weight: 700;
                      color: ${theme.midnight};
                      font-size: 1em;
                    `}
                  >
                    {item.mapName || 'Untitled Map'}
                  </div>
                  <div
                    css={css`
                      font-size: 0.85em;
                      color: ${theme.mono50};
                    `}
                  >
                    {item.sharedAt
                      ? getRelativeTime(item.sharedAt)
                      : '—'}
                  </div>
                </div>

                {/* Column 3: Right-aligned — shares, Copy link, collaborators */}
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 0.35em;
                    font-size: 0.85em;
                    color: ${theme.mono50};
                  `}
                >
                  <span
                    css={css`
                      display: inline-flex;
                      align-items: center;
                      gap: 0.35em;
                    `}
                  >
                    <span
                      css={css`
                        display: inline-flex;
                        width: 14px;
                        height: 14px;
                        color: ${theme.mono50};
                      `}
                    >
                      <LinkSVG />
                    </span>
                    {item.shareCount}{' '}
                    {item.shareCount === 1 ? 'share' : 'shares'}
                  </span>
                  {item.shareUrl && (
                    <button
                      type="button"
                      onClick={(e) => handleCopyLink(e, item)}
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        gap: 0.35em;
                        padding: 0.2em;
                        font-size: 0.95em;
                        color: ${theme.deepOcean};
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-weight: 500;
                        text-decoration: underline;
                        &:hover {
                          background: ${theme.lightOcean};
                        }
                      `}
                    >
                      <span
                        css={css`
                          display: inline-flex;
                          width: 14px;
                          height: 14px;
                        `}
                      >
                        <CopySVG />
                      </span>
                      {copiedMapId === item.mapId ? 'Copied!' : 'Copy link'}
                    </button>
                  )}
                  <span
                    css={css`
                      display: inline-flex;
                      align-items: center;
                      gap: 0.35em;
                    `}
                  >
                    <div
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        position: relative;
                        height: 24px;
                      `}
                    >
                      {item.collaborators.slice(0, 3).map((collab, i) => (
                        <div
                          key={i}
                          css={css`
                            width: 24px;
                            height: 24px;
                            border-radius: 50%;
                            background: ${theme.lightOcean};
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: 700;
                            font-size: 0.65em;
                            margin-left: ${i === 0 ? 0 : -10}px;
                            border: 2px solid ${theme.mono0};
                            overflow: hidden;
                            color: ${theme.deepOcean};
                          `}
                        >
                          {collab.profilePic ? (
                            <img
                              src={collab.profilePic}
                              alt=""
                              css={css`
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                              `}
                            />
                          ) : (
                            <span>
                              {collab.fname?.[0] ?? ''}
                              {collab.lname?.[0] ?? ''}
                            </span>
                          )}
                        </div>
                      ))}
                      {item.collaboratorCount > 3 && (
                        <div
                          css={css`
                            margin-left: -10px;
                            width: 24px;
                            height: 24px;
                            border-radius: 50%;
                            background: ${theme.mono0};
                            color: ${theme.flora};
                            font-size: 0.65em;
                            font-weight: 700;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border: 2px solid ${theme.flora};
                          `}
                        >
                          +{item.collaboratorCount - 3}
                        </div>
                      )}
                    </div>
                    {item.collaboratorCount}{' '}
                    {item.collaboratorCount === 1
                      ? 'collaborator'
                      : 'collaborators'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        css={css`
          padding: 1em;
          border-top: 1px solid ${theme.mono20};
        `}
      >
        <button
          disabled={isExpandDisabled}
          css={css`
            width: 100%;
            color: ${isExpandDisabled ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 6px;
            padding: 0.5em 1em;
            font-weight: 500;
            cursor: ${isExpandDisabled ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
            opacity: ${isExpandDisabled ? 0.6 : 1};
          `}
        >
          Expand
        </button>
      </div>
    </div>
  );
};
